import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../../../app/hooks";
import {Button, ButtonState} from "../../../button/Button";
import {CloseImageButton} from "../../../closeImageButton/CloseImageButton";
import {FileDropzone} from "../../../fileDropzone/FileDropzone";
import {LoadingModal} from "../../../loadingModal/LoadingModal";
import {RequestStatus} from "../../../../../api/apiConfiguration";
import {
    exportAssistant,
    exportRequestStatusSelector,
    importAssistant,
    importErrorSelector,
    importRequestStatusSelector,
    resetImportState
} from "../../../../modules/assistant/transfer/assistantTransferSlice";
import {
    showErrorToastNotification,
    showSuccessToastNotification
} from "../../../toastNotification/toastNotificationSlice";

import {AssistantImportResult} from "../../../../modules/assistant/transfer/assistantTransferApi";

import styles from "./ImportAssistantModal.module.css";

/** Matches the limit the platform enforces on an uploaded bundle. */
const MAX_BUNDLE_BYTES = 25 * 1024 * 1024;

/**
 * Turns the import result into one sentence for the success toast. Offers can be left alone when
 * they belong to a location shared with other assistants, and the operator needs to know that.
 */
const describeImport = (result: AssistantImportResult): string => {
    const message = `Configuration restored for ${result.assistantName}.`;
    return result.offersSkipped > 0
        ? `${message} ${result.offersSkipped} offer(s) were left unchanged on shared locations.`
        : message;
};

interface ImportAssistantModalProps {
    publicId: string
    assistantName?: string
    handleClose: (value?: any) => any
    /** Called once the import succeeds, so the page can reload what it shows. */
    onImported?: () => void
}

/**
 * Restores an assistant's configuration from a previously exported archive.
 *
 * The import replaces the whole configuration of the assistant being viewed, so the dialog leads
 * with a warning and an inline export button: taking a copy first is the only way back.
 */
export const ImportAssistantModal: React.FC<ImportAssistantModalProps> = ({
                                                                             publicId,
                                                                             assistantName,
                                                                             handleClose,
                                                                             onImported
                                                                         }) => {
    const dispatch = useAppDispatch();
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const importRequestStatus = useAppSelector(importRequestStatusSelector);
    const exportRequestStatus = useAppSelector(exportRequestStatusSelector);
    const importError = useAppSelector(importErrorSelector);

    const isImporting = importRequestStatus === RequestStatus.PENDING;
    const isExporting = exportRequestStatus === RequestStatus.PENDING;

    useEffect(() => {
        dispatch(resetImportState());
    }, [dispatch]);

    const handleExportFirst = async () => {
        try {
            const fileName = await dispatch(exportAssistant(publicId)).unwrap();
            dispatch(showSuccessToastNotification({message: `Configuration exported as ${fileName}`}));
        } catch (error) {
            dispatch(showErrorToastNotification({
                message: typeof error === "string" && error ? error : "Failed to export the assistant configuration"
            }));
        }
    };

    const handleImport = async () => {
        if (!selectedFile) {
            return;
        }

        try {
            const result = await dispatch(importAssistant({publicBotId: publicId, file: selectedFile})).unwrap();
            handleClose();
            dispatch(showSuccessToastNotification({message: describeImport(result)}));
            onImported?.();
        } catch {
            // The failure message is already in the store and is rendered inside the dialog, where
            // it stays readable while the operator decides what to do next.
        }
    };

    if (isImporting) {
        return <LoadingModal
            title={"Importing configuration"}
            subtitle={"This can take a moment. Please keep this window open."}
        />;
    }

    return <div className={styles.wrapper}>
        <div className={styles.titleWrapper}>
            <span className={styles.title}>Import configuration</span>
            <CloseImageButton onClose={handleClose}/>
        </div>

        <div className={styles.warning} role="note">
            <span className={styles.warningTitle}>This replaces the current configuration</span>
            <span className={styles.warningBody}>
                Everything on {assistantName ? `"${assistantName}"` : "this assistant"} - settings, tools,
                appearance, offers, franchises and locations - is overwritten by the uploaded file.
                Export the current configuration first so you can restore it if you need to.
                Conversations and transcripts are not affected.
            </span>
            <Button
                title={"Export current configuration"}
                iconSrc={"/exportIcon.svg"}
                state={isExporting ? ButtonState.DISABLED : ButtonState.SECONDARY}
                handleClick={handleExportFirst}
            />
        </div>

        <FileDropzone
            accept={".zip"}
            maxSizeBytes={MAX_BUNDLE_BYTES}
            title={selectedFile ? selectedFile.name : "Drop the export file here, or click to browse"}
            subtitle={selectedFile
                ? "Select a different file to replace this one"
                : "ZIP archive produced by the Export action"}
            onFileSelected={setSelectedFile}
        />

        {importError && <span className={styles.error} role="alert">{importError}</span>}

        <div className={styles.buttonsWrapper}>
            <Button
                title={"Cancel"}
                state={ButtonState.SECONDARY}
                handleClick={handleClose}
            />
            <Button
                title={"Import"}
                state={selectedFile ? ButtonState.PRIMARY : ButtonState.DISABLED}
                handleClick={handleImport}
            />
        </div>
    </div>
}
